<script setup>
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { computed, ref, onMounted } from 'vue';
import {
  Blocks,
  SquareArrowOutUpRight,
  Settings,
  Check,
  CheckSquare,
  XCircle,
  ChevronDown,
} from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import { request } from '@/lib/api';

const props = defineProps({
  class: {
    type: String,
    default: '',
  },
  open: Boolean,
});

const emit = defineEmits(['update:open']);

const modelValue = ref('');
const libraryList = ref([]);
const isOpen = computed({
  get: () => props.open,
  set: (v) => emit('update:open', v),
});
const previewLibrary = ref(null);
const previewItems = ref([]);
const selectedItems = ref([]);

defineExpose({
  getLibrary,
});

function getLibrary() {
  const items = selectedItems.value;
  if (!items.length) return '';
  const content = items
    .map((item) => {
      return `@SeedeLibraryItem(${item.name}##${item.id})`;
    })
    .join('\n');

  return content;
}

async function loadLibraries() {
  const [list, publicList] = await Promise.all([request('/library'), request('/library/public')]);
  const allList = [
    ...publicList,
    ...list.filter((item) => !publicList.find((p) => p.id === item.id)),
  ];
  libraryList.value = allList;
  selectLibrary(allList[0].id);
}

async function loadPreviewItems(id) {
  try {
    const data = await request(`/library/${id}/items`);
    previewItems.value = data.map((item) => ({
      ...item,
    }));
  } catch (error) {
    console.error('Failed to load preview items:', error);
  }
}

onMounted(() => {
  loadLibraries();
});

const libraries = computed(() => {
  if (props.libraries) return props.libraries;
  return libraryList.value;
});

function selectLibrary(id) {
  modelValue.value = id;
  const selectedLib = libraries.value.find((lib) => lib.id === id);
  if (selectedLib) {
    previewLibrary.value = selectedLib;
    loadPreviewItems(id);
  } else {
    previewLibrary.value = null;
  }
}

function selectItem(item) {
  const index = selectedItems.value.findIndex((i) => i.id === item.id);
  if (index === -1) {
    // Item not in array, add it
    selectedItems.value = [...selectedItems.value, item];
  } else {
    // Item already in array, remove it
    selectedItems.value = selectedItems.value.filter((i) => i.id !== item.id);
  }
}

function getSelectedItemsByLib(lib) {
  return selectedItems.value.filter((item) => item.library_id === lib.id);
}

function selectAllItems() {
  if (!previewLibrary.value) return;
  // Add all items from current library that aren't already selected
  const currentLibItems = previewItems.value;
  const newSelectedItems = [...selectedItems.value];

  currentLibItems.forEach((item) => {
    if (!selectedItems.value.some((i) => i.id === item.id)) {
      newSelectedItems.push(item);
    }
  });

  selectedItems.value = newSelectedItems;
}

function deselectAllItems() {
  if (!previewLibrary.value) return;
  // Remove all items from current library
  selectedItems.value = selectedItems.value.filter(
    (item) => item.library_id !== previewLibrary.value.id,
  );
}

function handleLibraryMouseEnter(event, libraryId) {
  if (modelValue.value !== libraryId) {
    event.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
  }
}

function handleLibraryMouseLeave(event, libraryId) {
  if (modelValue.value !== libraryId) {
    event.currentTarget.style.backgroundColor = '';
  }
}
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <div
        :class="
          cn(
            'group flex items-center gap-1 px-3 py-2 rounded-full border border-black/5 cursor-pointer hover:bg-black/5 hover:border-black/20 data-[state=open]:bg-black/5 data-[state=open]:border-black/20 transition-colors duration-200',
            props.class,
          )
        "
      >
        <Blocks class="size-4 block text-black/60" />
        <div class="hidden md:flex gap-0 items-center flex-1">
          <span
            class="text-xs truncate max-w-[120px] text-black/60 group-hover:text-black/90 transition-colors font-medium"
          >
            {{ $t('create.library.title') }}
          </span>
          <span
            v-if="selectedItems.length > 0"
            class="inline-flex items-center justify-center w-4 h-4 text-xs font-medium bg-[#86EEBA] rounded-full"
          >
            {{ selectedItems.length }}
          </span>
        </div>
        <ChevronDown class="size-4 text-black/30 group-hover:text-black/90 transition-colors" />
      </div>
    </PopoverTrigger>
    <PopoverContent
      class="w-[600px] h-[400px] p-0 rounded-[16px] backdrop-blur-[40px]"
      style="
        background-color: rgba(255, 255, 255, 0.8);
        border: 1px solid rgba(0, 0, 0, 0.07);
        box-shadow:
          0 20px 25px -5px rgba(0, 0, 0, 0.1),
          0 8px 10px -6px rgba(0, 0, 0, 0.1);
      "
    >
      <div class="flex h-[400px] w-[600px]">
        <!-- Library List -->
        <div
          class="w-[240px] flex flex-col p-3 border-r border-black/5 bg-black/5/50 backdrop-blur-sm"
        >
          <div class="flex flex-col space-y-1.5 grow overflow-y-auto">
            <div
              v-for="library in libraries"
              :key="library.id"
              @click="selectLibrary(library.id)"
              class="px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200 mb-0.5"
              :style="modelValue === library.id ? 'background-color: rgba(0, 0, 0, 0.05)' : ''"
              @mouseenter="handleLibraryMouseEnter($event, library.id)"
              @mouseleave="handleLibraryMouseLeave($event, library.id)"
            >
              <div class="flex w-full justify-between items-center">
                <span
                  class="text-sm font-normal tracking-tight"
                  :class="modelValue === library.id ? 'text-black' : 'text-black/50'"
                >
                  {{ library.name }}
                </span>
                <span
                  class="text-xs font-normal"
                  :class="modelValue === library.id ? 'text-black' : 'text-black/50'"
                >
                  {{
                    $t('create.library.itemCount', {
                      selected: getSelectedItemsByLib(library).length,
                      total: library.itemCount,
                    })
                  }}
                </span>
              </div>
            </div>
          </div>
          <router-link to="/library">
            <div
              class="px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200"
              @mouseenter="$event.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'"
              @mouseleave="$event.currentTarget.style.backgroundColor = ''"
            >
              <div class="flex items-center gap-2">
                <Settings class="size-4 text-black/60" />
                <span class="text-sm font-normal text-black/50 tracking-tight">
                  {{ $t('create.library.manage') }}
                </span>
                <SquareArrowOutUpRight class="size-4 text-black/50 ml-auto" />
              </div>
            </div>
          </router-link>
        </div>

        <!-- Preview Section -->
        <div class="flex-1 p-4 overflow-y-auto h-full w-[360px] bg-white">
          <div v-if="previewLibrary" class="space-y-4">
            <!-- Selection Controls -->
            <div class="flex items-center justify-between mb-3 pb-2 border-b border-black/5">
              <div class="text-sm font-medium text-black/70">
                {{ previewLibrary.name }}
              </div>
              <div class="flex gap-2">
                <button
                  @click="selectAllItems"
                  class="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                  :title="$t('library.selectAll')"
                >
                  <CheckSquare class="size-3.5" />
                  <span class="hidden sm:inline">{{ $t('create.library.selectAll') }}</span>
                </button>
                <button
                  @click="deselectAllItems"
                  class="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-black/5 text-black/60 hover:bg-black/5 transition-colors"
                  :title="$t('library.deselectAll')"
                >
                  <XCircle class="size-3.5" />
                  <span class="hidden sm:inline">{{ $t('create.library.deselectAll') }}</span>
                </button>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div
                v-for="item in previewItems"
                :key="item.id"
                :class="[
                  'bg-white rounded-lg overflow-hidden transition-all duration-200 relative cursor-pointer',
                  'hover:shadow-md hover:translate-y-[-2px]',
                  selectedItems.findIndex((i) => i.id === item.id) !== -1
                    ? 'ring-2 ring-[#86EEBA]'
                    : 'hover:ring-1 hover:ring-blue-200',
                ]"
                @click="selectItem(item)"
                :title="$t('create.library.addToDesign')"
              >
                <!-- Green check icon for selected items -->
                <div
                  v-if="selectedItems.findIndex((i) => i.id === item.id) !== -1"
                  class="absolute top-1 right-1 z-10 bg-green-500 rounded-full p-0.5"
                >
                  <Check class="size-3 text-white" />
                </div>

                <div class="w-full h-24 p-2 bg-black/60 relative group">
                  <div
                    class="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                  >
                    <span
                      class="text-xs font-medium bg-white/90 px-2 py-1 rounded-md shadow-sm text-black/70"
                    >
                      {{ $t('create.library.clickToSelect') || 'Click to select' }}
                    </span>
                  </div>
                  <img
                    :src="item.thumbnail"
                    :alt="$t('library.preview')"
                    class="w-full h-full object-contain rounded"
                  />
                </div>

                <div class="p-2">
                  <p class="text-sm font-medium truncate text-black/80">
                    {{ item.name }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="flex items-center justify-center h-full">
            <p class="text-black/50">{{ $t('create.library.select') }}</p>
          </div>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
